"use client"

import { createContext, useContext, useEffect, useState } from "react"
import { useRouter } from "next/navigation"
import { supabase } from "@/lib/supabase/client"
type Plan = "free" | "pro" | "agency"

type AuthMode = "login" | "signup"

type AuthContextType = {
  user: any
  plan: Plan
  isPaid: boolean
  loading: boolean
  openAuth: (mode?: AuthMode) => void
  closeAuth: () => void
  showAuth: boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

// Cache the last-known plan in localStorage so a fresh page load
// (or returning from an external billing portal) hydrates the
// PlanBadge with the correct value instead of flashing "FREE" while
// the async profile fetch resolves.
const PLAN_CACHE_KEY = "db_last_plan"

function readCachedPlan(): Plan {
  if (typeof window === "undefined") return "free"
  try {
    const v = window.localStorage.getItem(PLAN_CACHE_KEY)
    if (v === "pro" || v === "agency" || v === "free") return v
  } catch {}
  return "free"
}

function writeCachedPlan(p: Plan) {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(PLAN_CACHE_KEY, p)
  } catch {}
}

export function AuthProvider({ children }: any) {
  const router = useRouter()
  const [user, setUser] = useState<any>(null)
  // Lazy-init from cache so SSR ('free') and CSR-hydrate ('agency')
  // don't mismatch — we only read the cache after mount via state init,
  // which runs once on the client.
  const [isPaid, setIsPaid] = useState(false)
  const [plan, setPlan] = useState<Plan>("free")
  const [loading, setLoading] = useState(true)

  // After mount: pull the cached plan into state so the badge renders
  // the optimistic value immediately. The real fetch in loadUser will
  // overwrite it within ~200ms either way.
  useEffect(() => {
    const cached = readCachedPlan()
    if (cached !== "free") {
      setPlan(cached)
      setIsPaid(cached === "pro" || cached === "agency")
    }
  }, [])

  // 🔥 cargar user inicial + profile
  const loadUser = async (currentUser: any) => {
    if (!currentUser) {
      setUser(null)
      setIsPaid(false)
      setPlan("free")
      writeCachedPlan("free")
      setLoading(false)
      return
    }

    setUser(currentUser)

    // get plan
    const { data, error } = await supabase
      .from("profiles")
      .select("plan")
      .eq("id", currentUser.id)
      .maybeSingle()

    if (error) console.error("❌ auth-context profile fetch:", error)

    const userPlan: Plan = data?.plan || "free"

    setPlan(userPlan)
    setIsPaid(userPlan === "pro" || userPlan === "agency")
    writeCachedPlan(userPlan)
    setLoading(false)
  }

  useEffect(() => {
    let profileChannel: ReturnType<typeof supabase.channel> | null = null

    const subscribeToProfile = (userId: string) => {
      // Stay in sync with server-side plan changes (admin downgrade,
      // Paddle webhook upgrade, etc.) without forcing a page reload.
      profileChannel = supabase
        .channel(`profile:${userId}`)
        .on(
          "postgres_changes",
          { event: "UPDATE", schema: "public", table: "profiles", filter: `id=eq.${userId}` },
          (payload) => {
            const next = (payload.new as { plan?: Plan })?.plan ?? "free"
            setPlan(next)
            setIsPaid(next === "pro" || next === "agency")
            writeCachedPlan(next)
          }
        )
        .subscribe()
    }

    // inicial
    supabase.auth.getUser().then(({ data }) => {
      loadUser(data.user)
      if (data.user) subscribeToProfile(data.user.id)
    })

    // listener
    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (_event === "PASSWORD_RECOVERY") {
          router.push("/auth/reset-password")
          return
        }

        loadUser(session?.user)

        if (profileChannel) {
          supabase.removeChannel(profileChannel)
          profileChannel = null
        }
        if (session?.user) subscribeToProfile(session.user.id)
      }
    )

    return () => {
      listener.subscription.unsubscribe()
      if (profileChannel) supabase.removeChannel(profileChannel)
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        plan,
        isPaid,
        loading,
        openAuth: (mode?: AuthMode) =>
          router.push(mode === "signup" ? "/login?mode=signup" : "/login"),
        closeAuth: () => {},
        showAuth: false,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error("useAuth must be used inside AuthProvider")
  return context
}